import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClaim, CreateClaimPayload } from '../api/claims';
import { Client } from '../api/clients';
import ClientSearch from '../components/ClientSearch';
import LenderSearch from '../components/LenderSearch';
import './CreateClaim.css';

interface FormData {
  // Client linking
  linkToExistingClient: boolean;
  clientId: string;
  useNewClReference: boolean;  // Create as own claim with fresh CL reference
  // Personal info
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  dateOfBirth: string;
  previouslyKnownNames: string[];
  // Address
  addressLine1: string;
  addressLine2: string;
  town: string;
  county: string;
  city: string;
  postcode: string;
  previousAddresses: string[];
  // Credit agreement
  craFinanceLender: string;
  craAgreementNumberOriginal: string;
  craCompanyType: string;
  craCreditStartDate: string;
  craCreditEndDate: string;
  defendantReference: string;
  commissionAmount: string;
  commissionType: string;
  newLenderEmail: string;
}

const TITLE_OPTIONS = ['', 'Mr', 'Mrs', 'Miss', 'Ms', 'Dr'];
const COMMISSION_TYPE_OPTIONS = ['', 'Percentage', 'Fixed Amount', 'Unknown'];

const CreateClaim: React.FC = () => {
  const navigate = useNavigate();
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<FormData>({
    linkToExistingClient: false,
    clientId: '',
    useNewClReference: false,
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
    previouslyKnownNames: [],
    addressLine1: '',
    addressLine2: '',
    town: '',
    county: '',
    city: '',
    postcode: '',
    previousAddresses: [],
    craFinanceLender: '',
    craAgreementNumberOriginal: '',
    craCompanyType: '',
    craCreditStartDate: '',
    craCreditEndDate: '',
    defendantReference: '',
    commissionAmount: '',
    commissionType: '',
    newLenderEmail: '',
  });

  // Handle input change
  const handleInputChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [validationErrors]);

  // Handle client selection from ClientSearch
  const handleClientSelect = useCallback((client: Client | null) => {
    setSelectedClient(client);

    if (client) {
      // Auto-populate fields from client data
      setFormData((prev) => ({
        ...prev,
        clientId: client.id,
        firstName: client.firstName || prev.firstName,
        lastName: client.lastName || prev.lastName,
        email: client.email || prev.email,
        phoneNumber: client.phoneNumber || prev.phoneNumber,
        dateOfBirth: client.dateOfBirth || prev.dateOfBirth,
        addressLine1: client.addressLine1 || prev.addressLine1,
        addressLine2: client.addressLine2 || prev.addressLine2,
        city: client.city || prev.city,
        postcode: client.postcode || prev.postcode,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        clientId: '',
      }));
    }
  }, []);

  // Handle lender selection from LenderSearch
  const handleLenderSelect = useCallback((lenderName: string, lenderEmail?: string) => {
    setFormData((prev) => ({
      ...prev,
      craFinanceLender: lenderName,
      newLenderEmail: lenderEmail || '',
    }));

    if (validationErrors.craFinanceLender) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.craFinanceLender;
        return newErrors;
      });
    }
  }, [validationErrors]);

  // Handle array field add
  const handleAddArrayItem = useCallback((fieldName: 'previouslyKnownNames' | 'previousAddresses') => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: [...prev[fieldName], ''],
    }));
  }, []);

  // Handle array field remove
  const handleRemoveArrayItem = useCallback((
    fieldName: 'previouslyKnownNames' | 'previousAddresses',
    index: number
  ) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index),
    }));
  }, []);

  // Handle array field value change
  const handleArrayItemChange = useCallback((
    fieldName: 'previouslyKnownNames' | 'previousAddresses',
    index: number,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: prev[fieldName].map((item, i) => (i === index ? value : item)),
    }));
  }, []);

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      }
    }
    if (!formData.addressLine1.trim()) {
      errors.addressLine1 = 'Address line 1 is required';
    }
    if (!formData.postcode.trim()) {
      errors.postcode = 'Postcode is required';
    }
    if (!formData.craFinanceLender.trim()) {
      errors.craFinanceLender = 'Lender is required';
    }
    if (!formData.craAgreementNumberOriginal.trim()) {
      errors.craAgreementNumberOriginal = 'Agreement number is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Build the payload
      const payload: CreateClaimPayload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        addressLine1: formData.addressLine1.trim(),
        postcode: formData.postcode.trim(),
        craFinanceLender: formData.craFinanceLender.trim(),
        craAgreementNumberOriginal: formData.craAgreementNumberOriginal.trim(),
      };

      // Add optional fields if they have values
      if (formData.linkToExistingClient && formData.clientId) {
        payload.clientId = formData.clientId;
        // If creating as own claim, flag to use fresh CL reference
        if (formData.useNewClReference) {
          payload.useNewClReference = true;
        }
      }
      if (formData.title) {
        payload.title = formData.title;
      }
      if (formData.phoneNumber.trim()) {
        payload.phoneNumber = formData.phoneNumber.trim();
      }
      if (formData.dateOfBirth) {
        payload.dateOfBirth = formData.dateOfBirth;
      }
      if (formData.previouslyKnownNames.filter((n) => n.trim()).length > 0) {
        payload.previouslyKnownNames = formData.previouslyKnownNames.filter((n) => n.trim());
      }
      if (formData.addressLine2.trim()) {
        payload.addressLine2 = formData.addressLine2.trim();
      }
      if (formData.town.trim()) {
        payload.town = formData.town.trim();
      }
      if (formData.county.trim()) {
        payload.county = formData.county.trim();
      }
      if (formData.city.trim()) {
        payload.city = formData.city.trim();
      }
      if (formData.previousAddresses.filter((a) => a.trim()).length > 0) {
        payload.previousAddresses = formData.previousAddresses.filter((a) => a.trim());
      }
      if (formData.craCompanyType.trim()) {
        payload.craCompanyType = formData.craCompanyType.trim();
      }
      if (formData.craCreditStartDate) {
        payload.craCreditStartDate = formData.craCreditStartDate;
      }
      if (formData.craCreditEndDate) {
        payload.craCreditEndDate = formData.craCreditEndDate;
      }
      if (formData.defendantReference.trim()) {
        payload.defendantReference = formData.defendantReference.trim();
      }
      if (formData.commissionAmount) {
        const amount = parseFloat(formData.commissionAmount);
        if (!isNaN(amount)) {
          payload.commissionAmount = amount;
        }
      }
      if (formData.commissionType) {
        payload.commissionType = formData.commissionType;
      }
      if (formData.newLenderEmail.trim()) {
        payload.newLenderEmail = formData.newLenderEmail.trim();
      }

      const newClaim = await createClaim(payload);

      // Navigate to home with the new claim ID to open it
      navigate('/', { state: { openClaimId: newClaim.id } });
    } catch (err: any) {
      console.error('Error creating claim:', err);
      setError(err.message || 'Failed to create claim. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="create-claim-page">
      <div className="page-header">
        <div className="header-left">
          <button
            type="button"
            className="back-button"
            onClick={() => navigate('/')}
          >
            &larr; Back to Claims
          </button>
          <h1>Create New Claim</h1>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="create-claim-form">
        {/* Section 1: Client Selection */}
        <section className="form-section">
          <h2>Client Selection</h2>
          <div className="form-row">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="linkToExistingClient"
                checked={formData.linkToExistingClient}
                onChange={handleInputChange}
              />
              Link to existing client
            </label>
          </div>
          {formData.linkToExistingClient && (
            <>
              <div className="form-row">
                <label>Search Client</label>
                <ClientSearch
                  onSelect={handleClientSelect}
                  selectedClient={selectedClient}
                />
              </div>
              <div className="form-row">
                <label className="checkbox-label own-claim-checkbox">
                  <input
                    type="checkbox"
                    name="useNewClReference"
                    checked={formData.useNewClReference}
                    onChange={handleInputChange}
                  />
                  <span>Create as own claim (new CL reference)</span>
                  <span className="checkbox-hint">
                    Use this for claims you own fully. Creates a fresh CL number instead of using the client's existing reference sequence.
                  </span>
                </label>
              </div>
            </>
          )}
        </section>

        {/* Section 2: Personal Info */}
        <section className="form-section">
          <h2>Personal Information</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="title">Title</label>
              <select
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
              >
                {TITLE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option || 'Select...'}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="firstName">
                First Name <span className="required">*</span>
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleInputChange}
                className={validationErrors.firstName ? 'error' : ''}
              />
              {validationErrors.firstName && (
                <span className="field-error">{validationErrors.firstName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="lastName">
                Last Name <span className="required">*</span>
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleInputChange}
                className={validationErrors.lastName ? 'error' : ''}
              />
              {validationErrors.lastName && (
                <span className="field-error">{validationErrors.lastName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="email">
                Email <span className="required">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                className={validationErrors.email ? 'error' : ''}
              />
              {validationErrors.email && (
                <span className="field-error">{validationErrors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="phoneNumber">Phone Number</label>
              <input
                id="phoneNumber"
                name="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="dateOfBirth">Date of Birth</label>
              <input
                id="dateOfBirth"
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Previously Known Names (array field) */}
          <div className="form-group array-field">
            <label>Previously Known Names</label>
            {formData.previouslyKnownNames.map((name, index) => (
              <div key={index} className="array-item">
                <input
                  type="text"
                  value={name}
                  onChange={(e) =>
                    handleArrayItemChange('previouslyKnownNames', index, e.target.value)
                  }
                  placeholder="Previous name"
                />
                <button
                  type="button"
                  className="btn btn-remove"
                  onClick={() => handleRemoveArrayItem('previouslyKnownNames', index)}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-add"
              onClick={() => handleAddArrayItem('previouslyKnownNames')}
            >
              + Add Previous Name
            </button>
          </div>
        </section>

        {/* Section 3: Address */}
        <section className="form-section">
          <h2>Address</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label htmlFor="addressLine1">
                Address Line 1 <span className="required">*</span>
              </label>
              <input
                id="addressLine1"
                name="addressLine1"
                type="text"
                value={formData.addressLine1}
                onChange={handleInputChange}
                className={validationErrors.addressLine1 ? 'error' : ''}
              />
              {validationErrors.addressLine1 && (
                <span className="field-error">{validationErrors.addressLine1}</span>
              )}
            </div>

            <div className="form-group full-width">
              <label htmlFor="addressLine2">Address Line 2</label>
              <input
                id="addressLine2"
                name="addressLine2"
                type="text"
                value={formData.addressLine2}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="town">Town</label>
              <input
                id="town"
                name="town"
                type="text"
                value={formData.town}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="county">County</label>
              <input
                id="county"
                name="county"
                type="text"
                value={formData.county}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                id="city"
                name="city"
                type="text"
                value={formData.city}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="postcode">
                Postcode <span className="required">*</span>
              </label>
              <input
                id="postcode"
                name="postcode"
                type="text"
                value={formData.postcode}
                onChange={handleInputChange}
                className={validationErrors.postcode ? 'error' : ''}
              />
              {validationErrors.postcode && (
                <span className="field-error">{validationErrors.postcode}</span>
              )}
            </div>
          </div>

          {/* Previous Addresses (array field) */}
          <div className="form-group array-field">
            <label>Previous Addresses</label>
            {formData.previousAddresses.map((address, index) => (
              <div key={index} className="array-item">
                <input
                  type="text"
                  value={address}
                  onChange={(e) =>
                    handleArrayItemChange('previousAddresses', index, e.target.value)
                  }
                  placeholder="Previous address"
                />
                <button
                  type="button"
                  className="btn btn-remove"
                  onClick={() => handleRemoveArrayItem('previousAddresses', index)}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-add"
              onClick={() => handleAddArrayItem('previousAddresses')}
            >
              + Add Previous Address
            </button>
          </div>
        </section>

        {/* Section 4: Credit Agreement */}
        <section className="form-section">
          <h2>Credit Agreement</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label htmlFor="craFinanceLender">
                Lender <span className="required">*</span>
              </label>
              <LenderSearch
                onSelect={handleLenderSelect}
                selectedLender={formData.craFinanceLender}
              />
              {validationErrors.craFinanceLender && (
                <span className="field-error">{validationErrors.craFinanceLender}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="craAgreementNumberOriginal">
                Agreement Number <span className="required">*</span>
              </label>
              <input
                id="craAgreementNumberOriginal"
                name="craAgreementNumberOriginal"
                type="text"
                value={formData.craAgreementNumberOriginal}
                onChange={handleInputChange}
                className={validationErrors.craAgreementNumberOriginal ? 'error' : ''}
              />
              {validationErrors.craAgreementNumberOriginal && (
                <span className="field-error">
                  {validationErrors.craAgreementNumberOriginal}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="craCompanyType">Company Type</label>
              <input
                id="craCompanyType"
                name="craCompanyType"
                type="text"
                value={formData.craCompanyType}
                onChange={handleInputChange}
                placeholder="e.g., Finance Company"
              />
            </div>

            <div className="form-group">
              <label htmlFor="craCreditStartDate">Credit Start Date</label>
              <input
                id="craCreditStartDate"
                name="craCreditStartDate"
                type="date"
                value={formData.craCreditStartDate}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="craCreditEndDate">Credit End Date</label>
              <input
                id="craCreditEndDate"
                name="craCreditEndDate"
                type="date"
                value={formData.craCreditEndDate}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="defendantReference">Defendant Reference</label>
              <input
                id="defendantReference"
                name="defendantReference"
                type="text"
                value={formData.defendantReference}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="commissionAmount">Commission Amount</label>
              <input
                id="commissionAmount"
                name="commissionAmount"
                type="number"
                step="0.01"
                value={formData.commissionAmount}
                onChange={handleInputChange}
                placeholder="0.00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="commissionType">Commission Type</label>
              <select
                id="commissionType"
                name="commissionType"
                value={formData.commissionType}
                onChange={handleInputChange}
              >
                {COMMISSION_TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option || 'Select...'}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* Submit Button */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/')}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating Claim...' : 'Create Claim'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateClaim;
